<template>
  <NuxtLayout name="auth">
    <template #form>
      <login />
      <InviteCodeModal v-model:is-invite-code-modal-open="isInviteCodeModalOpen" />
    </template>
    <template #media>
      <AuthAnimation />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { googleOAuthLoginApi } from '~/api/auth';
import AuthAnimation from '~/components/auth/AuthAnimation.vue';
import { useAuth } from '~/composables/useAuth';
import { AuthErrorCode, getAuthErrorCodes } from '~/constants/errorCodes';
import InviteCodeModal from '~/pages/login/_components/InviteCodeModal.vue';
import login from '~/pages/login/_components/Login.vue';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorWithStatus } from '~/types/api';
import { useLocalePath } from '#i18n';

const { t } = useI18n();

definePageMeta({
  layout: false,
});

const router = useRouter();
const route = useRoute();
const toastStore = useToastStore();
const { login: doLogin } = useAuth();
const localePath = useLocalePath();
const isInviteCodeModalOpen = ref(false);
const processedErrCode = ref<string | null>(null); // 追蹤已處理的 err_code，避免重複顯示
const processedAuthError = ref<string | null>(null); // 追蹤已處理的 authError，避免重複顯示

// 處理 err_code 的函數
const handleErrCode = (err_code_param: string | null | undefined) => {
  if (!err_code_param || err_code_param === null) return;

  // 如果已經處理過這個 err_code，則跳過
  if (processedErrCode.value === err_code_param) return;

  processedErrCode.value = err_code_param;

  // 將字串轉換為數字，並檢查是否為有效的錯誤碼
  const err_code = Number(err_code_param) as AuthErrorCode;

  // 檢查轉換後的數字是否為有效的 AuthErrorCode
  if (!Number.isNaN(err_code) && Object.values(AuthErrorCode).includes(err_code)) {
    try {
      const errorInfo = getAuthErrorCodes(err_code);
      toastStore.error(errorInfo.message);
    } catch (error) {
      console.error('Error getting auth error codes:', error);
      toastStore.error('login failed, please try again');
    }
  } else {
    // 如果錯誤碼無效，顯示通用錯誤訊息
    toastStore.error('login failed, please try again');
  }
};

onMounted(async () => {
  const guest_user_uuid = route.query.guest_user_uuid as string | undefined;
  const invite_code = route.query.invite_code as string | undefined;

  // 情境一：同時有 guest_user_uuid 與 invite_code（從註冊頁透過 Google 回來）
  // 這時不顯示 Modal，直接呼叫 googleOAuthLoginApi 完成註冊 + 登入
  if (guest_user_uuid && invite_code) {
    try {
      const response = await googleOAuthLoginApi({
        guest_user_uuid,
        invite_code,
      });

      const queryParams = new URLSearchParams();
      for (const [key, value] of Object.entries(route.query)) {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      }

      const queryString = queryParams.toString();
      const redirectPath = queryString
        ? `/organization/setup?${queryString}`
        : '/organization/setup';

      await doLogin(response.data.token, response.data.refresh_token, localePath(redirectPath));
      return;
    } catch (error) {
      console.error('Auto google OAuth login failed:', error);
      const apiError = error as ApiErrorWithStatus | Error;
      if ('message' in apiError && apiError.message) {
        toastStore.error(apiError.message);
      } else {
        toastStore.error('signup failed, please try again');
      }
      return router.push(localePath('/signup'));
      // 若自動流程失敗，退回顯示 Modal 讓使用者手動輸入
      // isInviteCodeModalOpen.value = true;
    }
  } else if (guest_user_uuid) {
    // 情境二：只有 guest_user_uuid（例如單純從 Google 登入但沒帶 invite_code）
    // 這時仍顯示 Modal，讓使用者輸入邀請碼
    isInviteCodeModalOpen.value = true;
  }

  // 處理 err_code
  const errCode = route.query.err_code;
  if (errCode) {
    // 處理陣列情況，取第一個非 null 值
    const errCodeStr = Array.isArray(errCode)
      ? errCode.find((v) => v !== null && v !== undefined) || null
      : errCode;
    handleErrCode(errCodeStr);
  }

  handleAuthError();
});

const handleAuthError = () => {
  // 處理 authError（從 middleware 傳遞過來的認證錯誤）
  const authError = route.query.authError;
  if (authError && processedAuthError.value !== authError) {
    processedAuthError.value = authError as string;

    // 根據錯誤類型顯示對應的錯誤訊息
    const errorMessageKey =
      authError === 'loginExpired'
        ? 'errorMessages.auth.loginExpired'
        : 'errorMessages.auth.unexpectedError';
    toastStore.error(t(errorMessageKey));

    // 清除 query 參數，避免重新載入時重複顯示
    const { authError: _authError, ...newQuery } = route.query;
    router.replace({ query: newQuery });
  }
};

// 監聽路由變化，確保 err_code 變化時也能處理
watch(
  () => route.query.err_code,
  (newErrCode) => {
    if (newErrCode) {
      // 處理陣列情況，取第一個非 null 值
      const errCodeStr = Array.isArray(newErrCode)
        ? newErrCode.find((v) => v !== null && v !== undefined) || null
        : newErrCode;
      handleErrCode(errCodeStr);
    }
  },
  { immediate: false }
);

watch(
  () => route.query.authError,
  (newAuthError) => {
    if (newAuthError) {
      handleAuthError();
    }
  },
  { immediate: false }
);
</script>